import {
  AnyIconProps,
  IconBraces,
  IconDocumentTif,
  IconImage,
} from '@hawkins/assets';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
} from '@hawkins/components';
import { ComponentType } from 'react';
import { downloadFDL } from './downloadFDL';
import { downloadFramingChartImage } from './downloadFramingChartImage';
import { FramingChartProps } from './types';
import { ValidationResult } from '../utilities/validation';
import styles from './FramingChartDownloader.module.css';
import { logFeatureEngaged } from 'src/domains/calculators/utilities/analytics';
import { ANALYTICS_CATEGORY, ANALYTICS_EVENTS } from '../../constants';

const DOWNLOAD_DISABLED_TOOLTIP =
  'Fix errors in the inputs before downloading.';

export default function FramingChartDownloader({
  desqueezedFramingChartProps,
  downloadValidation,
  framingChartProps,
}: {
  desqueezedFramingChartProps?: FramingChartProps;
  downloadValidation?: ValidationResult;
  framingChartProps: FramingChartProps;
}) {
  const downloadsDisabled = downloadValidation?.isValid === false;
  const tooltipLabel = downloadsDisabled ? DOWNLOAD_DISABLED_TOOLTIP : '';

  const isAnamorphic = framingChartProps.lensSqueezeFactor !== 1;

  const imageDownloadButton = (
    icon: ComponentType<AnyIconProps>,
    label: string,
    anamorphicCallback: () => void,
    sphericalCallback: () => void
  ) =>
    isAnamorphic ? (
      <Menu
        showCaret
        trigger={
          <Button
            aria-label="showCaret"
            icon={icon}
            disabled={downloadsDisabled}
          >
            {label}
          </Button>
        }
      >
        <MenuItem onClick={anamorphicCallback}>Anamorphic</MenuItem>
        <MenuItem onClick={sphericalCallback}>De-Squeezed</MenuItem>
      </Menu>
    ) : (
      <Button
        variant="primary"
        icon={icon}
        disabled={downloadsDisabled}
        onClick={() => sphericalCallback()}
      >
        {label}
      </Button>
    );

  return (
    <ButtonGroup className={styles.downloadButtonGroup}>
      <Tooltip label={tooltipLabel} placement="top">
        {imageDownloadButton(
          IconImage,
          'Download PNG',
          () => {
            logFeatureEngaged(
              ANALYTICS_CATEGORY,
              ANALYTICS_EVENTS.DOWNLOAD_PNG
            );
            downloadFramingChartImage('png', framingChartProps);
          },
          () => {
            logFeatureEngaged(
              ANALYTICS_CATEGORY,
              ANALYTICS_EVENTS.DOWNLOAD_PNG
            );
            downloadFramingChartImage(
              'png',
              desqueezedFramingChartProps || framingChartProps
            );
          }
        )}
      </Tooltip>
      <Tooltip label={tooltipLabel} placement="top">
        {imageDownloadButton(
          IconDocumentTif,
          'Download TIFF',
          () => {
            logFeatureEngaged(
              ANALYTICS_CATEGORY,
              ANALYTICS_EVENTS.DOWNLOAD_TIFF
            );
            downloadFramingChartImage('tiff', framingChartProps);
          },
          () => {
            logFeatureEngaged(
              ANALYTICS_CATEGORY,
              ANALYTICS_EVENTS.DOWNLOAD_TIFF
            );
            downloadFramingChartImage(
              'tiff',
              desqueezedFramingChartProps || framingChartProps
            );
          }
        )}
      </Tooltip>
      <Tooltip label={tooltipLabel} placement="top">
        <Button
          variant="primary"
          icon={IconBraces}
          onClick={() => {
            logFeatureEngaged(
              ANALYTICS_CATEGORY,
              ANALYTICS_EVENTS.DOWNLOAD_FDL
            );
            downloadFDL(framingChartProps);
          }}
          disabled={downloadsDisabled}
        >
          Download FDL
        </Button>
      </Tooltip>
    </ButtonGroup>
  );
}
